import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { formatLocalDate } from '../../../../app/utils'
import { NumericFormat } from 'react-number-format'
import { z } from 'zod'
import FormDatePickerInput from '../../../../components/Forms/FormDatePickerInput'
import FormSelect, { Option } from '../../../../components/Forms/FormSelect'
import RequiredAsterisk from '../../../../components/RequiredAsterisk/RequiredAsterisk'
import { SectionLabelTypography } from '../../../../components/Typographies/styles'
import { messages } from './messages'

interface CurrencyModalProps {
  open: boolean
  onClose: () => void
  onSave: (data: CurrencyPriceData) => void
  currencyOptions: Option[]
  initialData?: any
  isViewMode?: boolean
}

const currencySchema = z.object({
  currencyId: z.number({
    required_error: 'Currency is required',
  }),
  price: z
    .number({
      required_error: 'Price is required',
    })
    .min(0.01, 'Price must be greater than 0'),
  priceValidFrom: z.date({
    required_error: 'Valid From date is required',
  }),
  priceValidTo: z.date().optional().nullable(),
})

type CurrencyFormData = z.infer<typeof currencySchema>

interface CurrencyPriceData {
  currencyId: number
  price: number
  priceValidFrom: string
  priceValidTo: string | null
}

const CurrencyModal = ({
  open,
  onClose,
  onSave,
  currencyOptions,
  initialData,
  isViewMode = false,
}: CurrencyModalProps) => {

  const { formatMessage } = useIntl()
  const methods = useForm<CurrencyFormData>({
    resolver: zodResolver(currencySchema),
    defaultValues: initialData
      ? {
        currencyId: initialData.currencyId,
        price: initialData.price,
        priceValidFrom: initialData.priceValidFrom ? new Date(initialData.priceValidFrom) : new Date(),
        priceValidTo: initialData.priceValidTo ? new Date(initialData.priceValidTo) : undefined,
      }
      : {
        currencyId: undefined,
        price: undefined,
        priceValidFrom: new Date(),
        priceValidTo: undefined,
      },
    mode: 'onChange',
  })

  const {
    handleSubmit,
    formState: { isValid },
  } = methods

        const onSubmit = (data: CurrencyFormData) => {
          const formattedData: CurrencyPriceData = {
            currencyId: data.currencyId,
            price: data.price,
            priceValidFrom: data.priceValidFrom.toString(),
            priceValidTo: data.priceValidTo ? data.priceValidTo.toString() : '∞',
          }
          onSave(formattedData)
          onClose()
        }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        {isViewMode
          ? formatMessage(messages.viewCurrency)
          : initialData
            ? formatMessage(messages.editCurrency)
            : formatMessage(messages.addCurrencyTitle)
        }
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              {isViewMode ? (
                <Typography>
                  {currencyOptions.find(option => option.value === initialData?.currencyId)?.label || ''}
                </Typography>
              ) : (
                <FormSelect
                  name='currencyId'
                  label={formatMessage(messages.currency)}
                  options={currencyOptions}
                  disabled={isViewMode}
                  required
                />
              )}
            </Box>
            <Box sx={{ mb: 2 }}>
              <SectionLabelTypography sx={{ mb: 1 }}>
                {formatMessage(messages.activePrice)} {!isViewMode && <RequiredAsterisk />}
              </SectionLabelTypography>
              {isViewMode ? (
                <TextField
                  value={
                    initialData?.price?.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }) || ''
                  }
                  fullWidth
                  InputProps={{ readOnly: true }}
                  variant='filled'
                />
              ) : (
                <Controller
                  name='price'
                  control={methods.control}
                  render={({ field, fieldState }) => (
                    <NumericFormat
                      customInput={TextField}
                      fullWidth
                      decimalScale={2}
                      thousandSeparator={true}
                      allowNegative={false}
                      onValueChange={(vals) => {
                        field.onChange(vals.floatValue)
                      }}
                      value={field.value}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              )}
            </Box>
            <Box display='flex' columnGap={2} sx={{ mb: 2 }}>
              <Box flex={1}>
                {isViewMode ? (
                  <>
                    <SectionLabelTypography sx={{ mb: 1 }}>
                      {formatMessage(messages.validFrom)}
                    </SectionLabelTypography>
                    <TextField
                      value={
                        initialData?.priceValidFrom
                          ? formatLocalDate(initialData.priceValidFrom)
                          : ''
                      }
                      fullWidth
                      InputProps={{ readOnly: true }}
                      variant='filled'
                    />
                  </>
                ) : (
                  <FormDatePickerInput
                    name='priceValidFrom'
                    label={formatMessage(messages.validFrom)}
                    format='YYYY-MM-DD'
                    minDate={'1970/01/01'}
                  />
                )}
              </Box>
              <Box flex={1}>
                {isViewMode ? (
                  <>
                    <SectionLabelTypography sx={{ mb: 1 }}>
                      {formatMessage(messages.validTo)}
                    </SectionLabelTypography>
                    <TextField
                      value={
                        initialData?.priceValidTo === '∞'
                          ? '∞'
                          : initialData?.priceValidTo
                            ? formatLocalDate(initialData.priceValidTo)
                            : ''
                      }
                      fullWidth
                      InputProps={{ readOnly: true }}
                      variant='filled'
                    />
                  </>
                ) : (
                  <FormDatePickerInput
                    name='priceValidTo'
                    label={formatMessage(messages.validTo)}
                    format='YYYY-MM-DD'
                    allowInfinity={true}
                    minDate={'1970/01/01'}
                    infinityLabel='∞'
                  />
                )}
              </Box>
            </Box>
          </Box>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{isViewMode ? formatMessage(messages.close) : formatMessage(messages.cancel)}</Button>
        {!isViewMode && (
          <Button onClick={handleSubmit(onSubmit)} variant='contained' disabled={!isValid}>
            {initialData ? formatMessage(messages.update) : formatMessage(messages.add)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CurrencyModal
